// Subscribe: Premium with PayPal auto-renew. Opened from the top bar's Subscribe button and from Premium prompts.
// Already subscribed: when Premium runs to, and cancelling the renewals.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/auth/me';
import { config } from '@/config';
import { onPremiumRequired } from '@/api/client';
import {
  asPremiumFeature,
  isSubscribeOpen,
  loadPayPal,
  openSubscribe,
  useBilling,
  useSubscribeRequest,
  waitForSubscription,
  type SubscribeRequest,
} from './billing';
import { PlanStatus } from './PlanStatus';
import styles from '@/auth/ProfileDialog.module.css';
import own from './SubscribeDialog.module.css';

type Step = 'choose' | 'confirming' | 'done' | 'delayed' | 'failed';

export function SubscribeDialog() {
  const { t } = useTranslation();
  const request = useSubscribeRequest();
  const open = request !== null;
  const dialog = useRef<HTMLDialogElement>(null);
  const close = () => openSubscribe(false);

  // The API refused a Premium feature: offer Premium, saying which (unless Subscribe is already showing).
  useEffect(() => {
    onPremiumRequired((name) => {
      if (!isSubscribeOpen()) openSubscribe({ feature: asPremiumFeature(name) });
    });
    return () => onPremiumRequired(undefined);
  }, []);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="subscribe-title"
      onClose={close}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className={styles.header}>
        <h2 id="subscribe-title">{t('subscribe.title')}</h2>
        <button type="button" className={styles.close} aria-label={t('profile.close')} onClick={close}>
          ×
        </button>
      </div>
      {request && <SubscribeBody request={request} />}
    </dialog>
  );
}

function SubscribeBody({ request }: { request: SubscribeRequest | null }) {
  const { t } = useTranslation();
  const me = useMe();
  const billing = useBilling();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('choose');
  const buttons = useRef<HTMLDivElement>(null);
  const waiting = useRef<AbortController | null>(null);
  useEffect(() => () => waiting.current?.abort(), []);

  // The feature that opened Subscribe, as it was then: an API refusal while open doesn't change the offer.
  const [{ feature, then }] = useState(() => request ?? {});
  const refetch = billing.refetch;
  const plan = billing.data;
  const email = me.data?.email;
  const subscribed = plan?.autoRenew === true;
  const canSubscribe = !!plan?.planId && !!plan.payPalClientId && !!email && !subscribed;

  useEffect(() => {
    if (!canSubscribe || step !== 'choose' || !buttons.current) return;
    const container = buttons.current;
    let rendered: { close?(): Promise<void> } | undefined;
    let cancelled = false;
    loadPayPal(plan.payPalClientId)
      .then((paypal) => {
        if (cancelled) return;
        const paypalButtons = paypal.Buttons({
          style: { layout: 'vertical', shape: 'rect', label: 'subscribe' },
          // The integration API matches PayPal's webhook to the account by this email.
          createSubscription: (_data, actions) =>
            actions.subscription.create({ plan_id: plan.planId, custom_id: email }),
          onApprove: () => {
            setStep('confirming');
            const controller = new AbortController();
            waiting.current = controller;
            void waitForSubscription(refetch, controller.signal).then(async (ok) => {
              if (controller.signal.aborted) return;
              setStep(ok ? 'done' : 'delayed');
              await queryClient.invalidateQueries({ queryKey: ['me'] });
              // Premium is on: carry on with what the user chose.
              if (ok) then?.();
            });
          },
          onError: () => setStep('failed'),
        });
        rendered = paypalButtons;
        return paypalButtons.render(container);
      })
      .catch(() => !cancelled && setStep('failed'));
    return () => {
      cancelled = true;
      void rendered?.close?.().catch(() => undefined);
    };
  }, [canSubscribe, step, plan?.payPalClientId, plan?.planId, email, refetch, queryClient, then]);

  // Sign-in turned off for development: there is no account to subscribe.
  if (config.authDisabled) return <p className={own.note}>{t('subscribe.unavailable')}</p>;
  if (billing.isPending) return <p className={own.note}>{t('subscribe.loading')}</p>;
  if (billing.isError || !plan) {
    return (
      <p role="alert" className={styles.error}>
        {t('subscribe.loadError')}
      </p>
    );
  }

  if (step === 'confirming')
    return (
      <p className={own.note} role="status">
        {t('subscribe.confirming')}
      </p>
    );
  if (step === 'done' || step === 'delayed') {
    return (
      <>
        <p className={own.note} role="status">
          {step === 'done' ? t('subscribe.done', { plan: plan.planTitle }) : t('subscribe.delayed')}
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={() => openSubscribe(false)}>
            {t('subscribe.close')}
          </button>
        </div>
      </>
    );
  }

  if (subscribed) return <PlanStatus billing={plan} />;

  return (
    <>
      {feature && (
        <div className={own.chosen}>
          <div className={own.chosenTitle}>{t(`subscribe.feature.${feature}.title`)}</div>
          <p>{t(`subscribe.feature.${feature}.body`)}</p>
        </div>
      )}
      <div className={own.offer}>
        <div className={own.price}>{plan.price}</div>
        <div>
          <div className={own.planTitle}>{plan.planTitle}</div>
          <div className={own.renews}>{t('subscribe.autoRenew')}</div>
        </div>
      </div>
      <ul className={own.features}>
        <li>{t('subscribe.features.maps')}</li>
        <li>{t('subscribe.features.share')}</li>
        <li>{t('subscribe.features.suggest')}</li>
        <li>{t('subscribe.features.offline')}</li>
      </ul>
      {plan.premium && plan.premiumUntil && (
        <p className={own.note}>
          {t('subscribe.alreadyUntil', { date: new Date(plan.premiumUntil).toLocaleDateString() })}
        </p>
      )}
      {step === 'failed' && (
        <p role="alert" className={styles.error}>
          {t('subscribe.payPalError')}
        </p>
      )}
      {canSubscribe ? (
        <div ref={buttons} className={own.buttons} />
      ) : (
        <p className={own.note}>{t('subscribe.unavailable')}</p>
      )}
      <p className={own.small}>{t('subscribe.cancelAnyTime')}</p>
    </>
  );
}
